import { memo, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Download, FileSpreadsheet, Loader2, Search } from 'lucide-react';
import { DataState, Badge, Button, EditableCell, FilterTabs, Panel, Table, Thead, Tr, Td } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { FichaProducto } from '@/components/domain/FichaProducto';
import { exportarInventario, exportarPlanilla } from '@/lib/exports';
import { useSuppliers } from '@/lib/hooks';
import { useProducts, useRecalcularForecast, useUpdateProduct } from '@/lib/hooks';
import { inventoryStatus, type Tone } from '@/lib/status';
import { cn } from '@/lib/cn';
import { formatCompactCop, formatCop, formatNumber } from '@shared/format';
import type { InventoryStatus, Product } from '@shared/types';

type SortKey = 'estado' | 'cobertura' | 'valor';
const STATUS_RANK: Record<InventoryStatus, number> = {
  critico: 0, alerta: 1, saludable: 2, sobrestock: 3, sin_datos: 4,
};
const EDGE: Record<Tone, string> = {
  danger: 'border-l-danger', warning: 'border-l-warning', success: 'border-l-success',
  violet: 'border-l-violet', info: 'border-l-info', primary: 'border-l-primary', neutral: 'border-l-hairline',
};

/**
 * Se PAGINA, igual que la tabla de Configuración y por el mismo motivo.
 *
 * ⚠️ Con las celdas editables, pintar los 647 productos de una traba el navegador entero: no es
 * solo el costo de montar 6.470 celdas, es que un `<table>` de ancho automático **recalcula el
 * layout de TODA la tabla** cada vez que cambia el contenido de una celda. Escribir un número en
 * una fila obligaba al browser a re-medir las 647, y hasta el mouse se sentía trabado.
 */
const POR_PAGINA = 50;

const COLUMNS = [
  { key: 'estado', label: 'Estado' },
  { key: 'producto', label: 'Producto' },
  { key: 'catprov', label: 'Categoría / Proveedor' },
  { key: 'stock', label: 'Stock Actual', align: 'right' as const },
  { key: 'transito', label: 'En Tránsito', align: 'right' as const },
  { key: 'cobertura', label: 'Cobertura', align: 'right' as const, ayuda: 'cobertura' as const },
  { key: 'demanda', label: 'Demanda/Día', align: 'right' as const, ayuda: 'demandaDia' as const },
  // Los dos que se cargan a mano: acá se ven —y se EDITAN— junto al stock, que es donde se
  // nota si faltan (sin costo no hay valor de inventario, y sin tiempo de producción no se
  // sabe con cuánta anticipación reponer). Antes solo se editaban en Configuración: mandar a
  // alguien a otra pantalla a arreglar lo que está mirando es fricción sin motivo.
  { key: 'lead', label: 'Tiempo de Producción', align: 'right' as const, wrap: true, ayuda: 'tiempoProduccion' as const },
  { key: 'unitario', label: 'Valor Unitario', align: 'right' as const, wrap: true, ayuda: 'valorUnitario' as const },
  { key: 'valor', label: 'Valor Total', align: 'right' as const },
];

export default function InventoryPage() {
  const products = useProducts();
  const editar = useUpdateProduct();
  const recalcular = useRecalcularForecast();
  // El filtro vive en la URL, no sólo en memoria: así el Dashboard puede mandar a "los 121
  // críticos" con un clic, el botón de atrás vuelve a lo que estabas viendo y el enlace se
  // puede pasar por chat. Un estado que sólo vive en React no permite nada de eso.
  const [params, setParams] = useSearchParams();
  const filter = params.get('estado') ?? 'todos';
  const setFilter = (v: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (v === 'todos') p.delete('estado');
        else p.set('estado', v);
        return p;
      },
      { replace: true },
    );
  const [sort, setSort] = useState<SortKey>('estado');
  const [search, setSearch] = useState('');
  const [pagina, setPagina] = useState(0);
  const [editados, setEditados] = useState<Set<string>>(new Set());
  const [aviso, setAviso] = useState<{ texto: string; tono: 'ok' | 'error' } | null>(null);
  const [fichaSku, setFichaSku] = useState<string | null>(null);
  const proveedores = useSuppliers();
  const abrirFicha = useCallback((sku: string) => setFichaSku(sku), []);
  const cerrarFicha = useCallback(() => setFichaSku(null), []);

  // useCallback + fila memoizada: sin esto, guardar una celda vuelve a renderizar las 50 filas
  // con todos sus inputs adentro, que es justo lo que se siente como lag al tipear.
  const guardar = useCallback(
    async (sku: string, patch: { unitCost?: number; leadTimeDays?: number }) => {
      try {
        await editar.mutateAsync({ sku, patch });
        setEditados((prev) => new Set(prev).add(sku));
        setAviso({ texto: `${sku} guardado`, tono: 'ok' });
      } catch (e) {
        setAviso({ texto: (e as Error).message, tono: 'error' });
      }
    },
    [editar],
  );

  /** Filtrar, ordenar o buscar vuelve a la página 1: si no, se ve una tabla vacía. */
  const desdeElPrincipio = (fn: () => void) => {
    fn();
    setPagina(0);
  };

  return (
    <DataState query={products}>
      {(data) => {
        const filtered = filterSortSearch(data.items, filter, sort, search);
        const paginas = Math.max(1, Math.ceil(filtered.length / POR_PAGINA));
        const pagActual = Math.min(pagina, paginas - 1);
        const visibles = filtered.slice(pagActual * POR_PAGINA, (pagActual + 1) * POR_PAGINA);
        const tabs = [
          { key: 'todos', label: 'Todos', count: data.total },
          { key: 'critico', label: 'CRÍTICO', count: data.counts.critico ?? 0, tone: 'danger' as Tone },
          { key: 'alerta', label: 'ALERTA', count: data.counts.alerta ?? 0, tone: 'warning' as Tone },
          { key: 'saludable', label: 'SALUDABLE', count: data.counts.saludable ?? 0, tone: 'success' as Tone },
          { key: 'sobrestock', label: 'SOBRESTOCK', count: data.counts.sobrestock ?? 0, tone: 'violet' as Tone },
          // Sin venta en el B2B no hay cobertura calculable: van aparte, no como "saludable".
          { key: 'sin_datos', label: 'SIN DATOS', count: data.counts.sin_datos ?? 0, tone: 'neutral' as Tone },
        ];
        return (
          <>
            {/* El costo unitario viene vacío en los 724 productos del B2B, así que el valor total
                da 0: decirlo es mejor que mostrar "$0" como si el inventario no valiera nada. */}
            <PageHeader
              title="Semáforo de Inventario"
              subtitle={
                data.totalValue > 0
                  ? `${data.total} SKUs · Valor total: ${formatCompactCop(data.totalValue)} COP`
                  : `${data.total} SKUs · Valor total: sin costo unitario cargado en el B2B`
              }
              actions={
                <div className="flex items-center gap-2">
                  {/* ⚠️ Este botón existía desde antes y NO hacía nada: no tenía `onClick`. Se
                      apretaba y la pantalla se quedaba quieta, sin error ni aviso. */}
                  <Button
                    variant="subtle"
                    icon={<Download className="h-3.5 w-3.5" />}
                    onClick={() => exportarInventario(filtered, proveedores.data?.items ?? [])}
                    title="Baja lo que estás viendo, con los filtros aplicados"
                  >
                    Exportar
                  </Button>
                  <Button
                    variant="outline"
                    icon={<FileSpreadsheet className="h-3.5 w-3.5" />}
                    onClick={() => exportarPlanilla(data.items, proveedores.data?.items ?? [])}
                    title="Planilla para completar costo, tiempo de producción, MOQ y proveedor, y volver a subirla"
                  >
                    Planilla de carga
                  </Button>
                </div>
              }
            />

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <FilterTabs items={tabs} value={filter} onChange={(k) => desdeElPrincipio(() => setFilter(k))} />
              <div className="flex items-center gap-2">
                {/* El `capitalize` de CSS pone mayúscula en CADA palabra: salía "Ordenar Por
                    Estado", que en castellano está mal escrito. Y los tres botones repetían
                    "Ordenar por" tres veces; el rótulo lo dice una sola vez, al lado. */}
                <span className="text-2xs text-ink-faint">Ordenar por</span>
                {(
                  [
                    ['estado', 'Estado'],
                    ['cobertura', 'Menos cobertura'],
                    ['valor', 'Mayor valor'],
                  ] as [SortKey, string][]
                ).map(([k, etiqueta]) => (
                  <button
                    key={k}
                    aria-pressed={sort === k}
                    onClick={() => desdeElPrincipio(() => setSort(k))}
                    className={cn(
                      'rounded-lg border px-2.5 py-1.5 text-2xs font-medium transition-colors',
                      sort === k
                        ? 'border-primary/40 bg-primary-soft text-primary-text'
                        : 'border-hairline text-ink-muted hover:text-ink',
                    )}
                  >
                    {etiqueta}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
              <input
                value={search}
                onChange={(e) => desdeElPrincipio(() => setSearch(e.target.value))}
                placeholder="Buscar por SKU, producto o proveedor…"
                className="h-10 w-full rounded-xl border border-hairline bg-panel pl-10 pr-3 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
              />
            </div>

            {/* El tiempo de producción y el costo entran en el cálculo del punto de reorden, la
                fecha límite y el capital. Editarlos sin recalcular dejaría el forecast opinando
                con los valores viejos — pero recalcular en cada tecla son 647 productos, así que
                la decisión la toma el usuario cuando terminó de cargar. */}
            {editados.size > 0 && (
              <div className="surface flex flex-wrap items-center justify-between gap-3 border-l-2 border-l-primary p-3">
                <p className="text-xs text-ink-muted">
                  <strong className="text-ink-strong">
                    {editados.size} producto{editados.size === 1 ? '' : 's'} editado{editados.size === 1 ? '' : 's'}.
                  </strong>{' '}
                  El punto de reorden y las fechas de compra se calculan con estos valores.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    disabled={recalcular.isPending}
                    onClick={() =>
                      recalcular
                        .mutateAsync()
                        .then(() => {
                          setEditados(new Set());
                          setAviso({ texto: 'Forecast recalculado con los valores nuevos.', tono: 'ok' });
                        })
                        .catch((e) => setAviso({ texto: (e as Error).message, tono: 'error' }))
                    }
                    icon={
                      recalcular.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )
                    }
                  >
                    {recalcular.isPending ? 'Recalculando…' : 'Recalcular forecast'}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditados(new Set())} disabled={recalcular.isPending}>
                    Después
                  </Button>
                </div>
              </div>
            )}

            <FichaProducto sku={fichaSku} onCerrar={cerrarFicha} />

            {aviso && (
              <p className={cn('text-2xs', aviso.tono === 'ok' ? 'text-success-text' : 'text-danger-text')}>
                {aviso.texto}
              </p>
            )}

            <Panel>
              <Table>
                <Thead columns={COLUMNS} />
                <tbody>
                  {visibles.map((p) => (
                    <FilaProducto key={p.sku} producto={p} onGuardar={guardar} onAbrir={abrirFicha} />
                  ))}
                </tbody>
              </Table>

              {paginas > 1 && (
                <div className="flex items-center justify-between gap-3 border-t border-hairline px-4 py-3">
                  <p className="text-2xs text-ink-faint">
                    Mostrando {formatNumber(pagActual * POR_PAGINA + 1)}-
                    {formatNumber(Math.min((pagActual + 1) * POR_PAGINA, filtered.length))} de{' '}
                    {formatNumber(filtered.length)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" disabled={pagActual === 0} onClick={() => setPagina(pagActual - 1)}>
                      Anterior
                    </Button>
                    <span className="text-2xs text-ink-faint">
                      {pagActual + 1} / {paginas}
                    </span>
                    <Button
                      variant="outline"
                      disabled={pagActual >= paginas - 1}
                      onClick={() => setPagina(pagActual + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </Panel>
          </>
        );
      }}
    </DataState>
  );
}

/**
 * Fila memoizada: solo se vuelve a renderizar si cambia SU producto.
 *
 * `guardar` viene envuelto en useCallback justo para esto — si cambiara de identidad en cada
 * render, el memo no serviría de nada y volveríamos al lag.
 */
const FilaProducto = memo(function FilaProducto({
  producto: p,
  onGuardar,
  onAbrir,
}: {
  producto: Product;
  onGuardar: (sku: string, patch: { unitCost?: number; leadTimeDays?: number }) => void;
  onAbrir: (sku: string) => void;
}) {
  const sd = inventoryStatus[p.status];
  return (
    <Tr className={cn('border-l-2', EDGE[sd.tone])}>
      <Td>
        <Badge tone={sd.tone}>{sd.label}</Badge>
      </Td>
      <Td>
        <button
          onClick={() => onAbrir(p.sku)}
          className="block max-w-[22rem] text-left transition-colors hover:text-primary-text"
          title="Ver la ficha del producto"
        >
          <p className="truncate font-medium text-ink-strong hover:text-primary-text">{p.name}</p>
          <p className="font-mono text-2xs text-ink-faint">{p.sku}</p>
        </button>
      </Td>
      <Td>
        <p className="text-ink">{p.category}</p>
        <p className="text-2xs text-ink-faint">{p.supplier}</p>
      </Td>
      <Td align="right">
        <span className="font-semibold text-ink-strong">{p.currentStock}</span>{' '}
        <span className="text-2xs text-ink-faint">uds</span>
      </Td>
      <Td align="right">
        {p.inTransit > 0 ? (
          <span className="text-info-text">{p.inTransit}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </Td>
      <Td align="right">
        <span className="font-semibold text-ink-strong">{p.coverageDays}d</span>
      </Td>
      <Td align="right">
        <span className="text-ink">{p.demandPerDay}</span>
      </Td>
      <Td align="right">
        <EditableCell
          valor={p.leadTimeDays}
          formato={formatNumber}
          sufijo="días"
          onGuardar={(v) => onGuardar(p.sku, { leadTimeDays: v })}
        />
      </Td>
      {/* Un costo en 0 no es un dato: es que nadie lo cargó todavía (el B2B lo trae vacío).
          Se dice, en vez de mostrar "$0" como si fuera gratis. */}
      <Td align="right">
        <EditableCell
          valor={p.unitCost}
          formato={formatCop}
          sinCargar={p.unitCost === 0}
          onGuardar={(v) => onGuardar(p.sku, { unitCost: v })}
        />
      </Td>
      <Td align="right">
        {p.unitCost > 0 ? (
          <span className="font-semibold text-ink-strong">
            {formatCompactCop(p.unitCost * p.currentStock)}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </Td>
    </Tr>
  );
});

function filterSortSearch(items: Product[], filter: string, sort: SortKey, search: string): Product[] {
  const q = search.trim().toLowerCase();
  let out = items.filter((p) => (filter === 'todos' || p.status === filter));
  if (q) out = out.filter((p) => `${p.sku} ${p.name} ${p.supplier} ${p.category}`.toLowerCase().includes(q));
  const sorted = [...out];
  if (sort === 'estado') sorted.sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.coverageDays - b.coverageDays);
  if (sort === 'cobertura') sorted.sort((a, b) => a.coverageDays - b.coverageDays);
  if (sort === 'valor') sorted.sort((a, b) => b.unitCost * b.currentStock - a.unitCost * a.currentStock);
  return sorted;
}
